/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  AffiliationType,
  CareLevel,
  MissionRoleType,
} from 'src/graphql/generated/graphql.generated';
import {
  IdListItemRecord,
  IdListItemProps,
} from 'src/store/generated/id-list-item-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RequesterProps {
  id: string;
  affiliationType: AffiliationType | null;
  caller: string | null;
  roleId: string | null;
  missionRole: MissionRoleType | null;
  callbackNumber: string | null;
  email: string | null;
  facilityId: string | null;
  agencyLocationId: string | null;
  isNewAgencyLocation: boolean | null;
  agencyLocationName: string | null;
  referringCompanyId: string | null;
  referringAgencyLocationId: string | null;
  referralReasonId: string | null;
  referringRecordNumber: string | null;
  specialtyTeamNotes: string | null;
  specialtyTeamId: string | null;
  specialtyTeamMembers: List<IdListItemProps>;
  careLevelRequested: CareLevel | null;
  transferCenterId: string | null;
  transferCenterNotes: string | null;
  transportingAgencyLocationId: string | null;
  isDoublePatient: boolean | null;
  isWingToWing: boolean | null;
  vetNumber: string | null;
  requestId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class RequesterRecord extends Record<RequesterProps>({
  id: null as any,
  affiliationType: null as any,
  caller: null as any,
  roleId: null as any,
  missionRole: null as any,
  callbackNumber: null as any,
  email: null as any,
  facilityId: null as any,
  agencyLocationId: null as any,
  isNewAgencyLocation: null as any,
  agencyLocationName: null as any,
  referringCompanyId: null as any,
  referringAgencyLocationId: null as any,
  referralReasonId: null as any,
  referringRecordNumber: null as any,
  specialtyTeamNotes: null as any,
  specialtyTeamId: null as any,
  specialtyTeamMembers: null as any,
  careLevelRequested: null as any,
  transferCenterId: null as any,
  transferCenterNotes: null as any,
  transportingAgencyLocationId: null as any,
  isDoublePatient: null as any,
  isWingToWing: null as any,
  vetNumber: null as any,
  requestId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    affiliationType: AffiliationType | null;
    caller: string | null;
    roleId: string | null;
    missionRole: MissionRoleType | null;
    callbackNumber: string | null;
    email: string | null;
    facilityId: string | null;
    agencyLocationId: string | null;
    isNewAgencyLocation: boolean | null;
    agencyLocationName: string | null;
    referringCompanyId: string | null;
    referringAgencyLocationId: string | null;
    referralReasonId: string | null;
    referringRecordNumber: string | null;
    specialtyTeamNotes: string | null;
    specialtyTeamId: string | null;
    specialtyTeamMembers: any;
    careLevelRequested: CareLevel | null;
    transferCenterId: string | null;
    transferCenterNotes: string | null;
    transportingAgencyLocationId: string | null;
    isDoublePatient: boolean | null;
    isWingToWing: boolean | null;
    vetNumber: string | null;
    requestId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): RequesterRecord {
    if (json == null) {
      throw new Error('RequesterRecord.fromJS: parameter cannot be null');
    }
    return new RequesterRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RequesterJson.id',
      ),
      affiliationType: json.affiliationType,
      caller: json.caller,
      roleId: json.roleId,
      missionRole: json.missionRole,
      callbackNumber: json.callbackNumber,
      email: json.email,
      facilityId: json.facilityId,
      agencyLocationId: json.agencyLocationId,
      isNewAgencyLocation: json.isNewAgencyLocation,
      agencyLocationName: json.agencyLocationName,
      referringCompanyId: json.referringCompanyId,
      referringAgencyLocationId: json.referringAgencyLocationId,
      referralReasonId: json.referralReasonId,
      referringRecordNumber: json.referringRecordNumber,
      specialtyTeamNotes: json.specialtyTeamNotes,
      specialtyTeamId: json.specialtyTeamId,
      specialtyTeamMembers: List<IdListItemRecord>(
        getFromOrThrow(
          json,
          (j) => j.specialtyTeamMembers,
          'RequesterJson.specialtyTeamMembers',
        ).map((o: any) => new IdListItemRecord(o)),
      ),
      careLevelRequested: json.careLevelRequested,
      transferCenterId: json.transferCenterId,
      transferCenterNotes: json.transferCenterNotes,
      transportingAgencyLocationId: json.transportingAgencyLocationId,
      isDoublePatient: json.isDoublePatient,
      isWingToWing: json.isWingToWing,
      vetNumber: json.vetNumber,
      requestId: json.requestId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'RequesterJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    affiliationType: AffiliationType | null;
    caller: string | null;
    roleId: string | null;
    missionRole: MissionRoleType | null;
    callbackNumber: string | null;
    email: string | null;
    facilityId: string | null;
    agencyLocationId: string | null;
    isNewAgencyLocation: boolean | null;
    agencyLocationName: string | null;
    referringCompanyId: string | null;
    referringAgencyLocationId: string | null;
    referralReasonId: string | null;
    referringRecordNumber: string | null;
    specialtyTeamNotes: string | null;
    specialtyTeamId: string | null;
    specialtyTeamMembers: any;
    careLevelRequested: CareLevel | null;
    transferCenterId: string | null;
    transferCenterNotes: string | null;
    transportingAgencyLocationId: string | null;
    isDoublePatient: boolean | null;
    isWingToWing: boolean | null;
    vetNumber: string | null;
    requestId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [RequesterRecord, Error[]] {
    if (json == null) {
      throw new Error('RequesterRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RequesterRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RequesterJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      affiliationType: tryOrDefault(
        () => json.affiliationType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      caller: tryOrDefault(
        () => json.caller,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      roleId: tryOrDefault(
        () => json.roleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      missionRole: tryOrDefault(
        () => json.missionRole,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      callbackNumber: tryOrDefault(
        () => json.callbackNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      email: tryOrDefault(
        () => json.email,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      facilityId: tryOrDefault(
        () => json.facilityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      agencyLocationId: tryOrDefault(
        () => json.agencyLocationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isNewAgencyLocation: tryOrDefault(
        () => json.isNewAgencyLocation,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      agencyLocationName: tryOrDefault(
        () => json.agencyLocationName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      referringCompanyId: tryOrDefault(
        () => json.referringCompanyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      referringAgencyLocationId: tryOrDefault(
        () => json.referringAgencyLocationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      referralReasonId: tryOrDefault(
        () => json.referralReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      referringRecordNumber: tryOrDefault(
        () => json.referringRecordNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      specialtyTeamNotes: tryOrDefault(
        () => json.specialtyTeamNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      specialtyTeamId: tryOrDefault(
        () => json.specialtyTeamId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      specialtyTeamMembers: tryOrDefault(
        () =>
          List<IdListItemRecord>(
            getFromOrThrow(
              json,
              (j) => j.specialtyTeamMembers,
              'RequesterJson.specialtyTeamMembers',
            ).map((o: any) => new IdListItemRecord(o)),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      careLevelRequested: tryOrDefault(
        () => json.careLevelRequested,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transferCenterId: tryOrDefault(
        () => json.transferCenterId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transferCenterNotes: tryOrDefault(
        () => json.transferCenterNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transportingAgencyLocationId: tryOrDefault(
        () => json.transportingAgencyLocationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isDoublePatient: tryOrDefault(
        () => json.isDoublePatient,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isWingToWing: tryOrDefault(
        () => json.isWingToWing,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vetNumber: tryOrDefault(
        () => json.vetNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestId: tryOrDefault(
        () => json.requestId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'RequesterJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: RequesterProps) {
    super(props);
    if (props == null) {
      throw new Error('RequesterRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RequesterViewModelBase implements RequesterProps {
  protected readonly _props: RequesterProps;
  private _json?: RequesterProps;

  get id(): string {
    return this._props.id;
  }
  get affiliationType(): AffiliationType | null {
    return this._props.affiliationType;
  }
  get caller(): string | null {
    return this._props.caller;
  }
  get roleId(): string | null {
    return this._props.roleId;
  }
  get missionRole(): MissionRoleType | null {
    return this._props.missionRole;
  }
  get callbackNumber(): string | null {
    return this._props.callbackNumber;
  }
  get email(): string | null {
    return this._props.email;
  }
  get facilityId(): string | null {
    return this._props.facilityId;
  }
  get agencyLocationId(): string | null {
    return this._props.agencyLocationId;
  }
  get isNewAgencyLocation(): boolean | null {
    return this._props.isNewAgencyLocation;
  }
  get agencyLocationName(): string | null {
    return this._props.agencyLocationName;
  }
  get referringCompanyId(): string | null {
    return this._props.referringCompanyId;
  }
  get referringAgencyLocationId(): string | null {
    return this._props.referringAgencyLocationId;
  }
  get referralReasonId(): string | null {
    return this._props.referralReasonId;
  }
  get referringRecordNumber(): string | null {
    return this._props.referringRecordNumber;
  }
  get specialtyTeamNotes(): string | null {
    return this._props.specialtyTeamNotes;
  }
  get specialtyTeamId(): string | null {
    return this._props.specialtyTeamId;
  }
  get specialtyTeamMembers(): List<IdListItemProps> {
    return this._props.specialtyTeamMembers;
  }
  get careLevelRequested(): CareLevel | null {
    return this._props.careLevelRequested;
  }
  get transferCenterId(): string | null {
    return this._props.transferCenterId;
  }
  get transferCenterNotes(): string | null {
    return this._props.transferCenterNotes;
  }
  get transportingAgencyLocationId(): string | null {
    return this._props.transportingAgencyLocationId;
  }
  get isDoublePatient(): boolean | null {
    return this._props.isDoublePatient;
  }
  get isWingToWing(): boolean | null {
    return this._props.isWingToWing;
  }
  get vetNumber(): string | null {
    return this._props.vetNumber;
  }
  get requestId(): string | null {
    return this._props.requestId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAgency() {
    return this._props.affiliationType === AffiliationType.Agency;
  }
  public get isCompetitor() {
    return this._props.affiliationType === AffiliationType.Competitor;
  }
  public get isInternal() {
    return this._props.affiliationType === AffiliationType.Internal;
  }
  public get isOther() {
    return this._props.affiliationType === AffiliationType.Other;
  }
  public get isPsap() {
    return this._props.affiliationType === AffiliationType.Psap;
  }
  public get isReceivingFacility() {
    return this._props.affiliationType === AffiliationType.ReceivingFacility;
  }
  public get isSendingFacility() {
    return this._props.affiliationType === AffiliationType.SendingFacility;
  }
  public get isSisterCompany() {
    return this._props.affiliationType === AffiliationType.SisterCompany;
  }
  public get isUnspecified() {
    return this._props.affiliationType === AffiliationType.Unspecified;
  }
  public get isANMC() {
    return this._props.missionRole === MissionRoleType.ANMC;
  }
  public get isCharter() {
    return this._props.missionRole === MissionRoleType.Charter;
  }
  public get isFlightFollowing() {
    return this._props.missionRole === MissionRoleType.FlightFollowing;
  }
  public get isFullService() {
    return this._props.missionRole === MissionRoleType.FullService;
  }
  public get isHospice() {
    return this._props.missionRole === MissionRoleType.Hospice;
  }
  public get isMassCasualty() {
    return this._props.missionRole === MissionRoleType.MassCasualty;
  }
  public get isPrecautionaryEvacuation() {
    return this._props.missionRole === MissionRoleType.PrecautionaryEvacuation;
  }
  public get isQuote() {
    return this._props.missionRole === MissionRoleType.Quote;
  }
  public get isQuoteOrNonEmergent() {
    return this._props.missionRole === MissionRoleType.QuoteOrNonEmergent;
  }
  public get isSEARHC() {
    return this._props.missionRole === MissionRoleType.SEARHC;
  }
  public get isSupplemental() {
    return this._props.missionRole === MissionRoleType.Supplemental;
  }
  public get isTeamFlight() {
    return this._props.missionRole === MissionRoleType.TeamFlight;
  }
  public get isVetRide() {
    return this._props.missionRole === MissionRoleType.VetRide;
  }
  public get isALS() {
    return this._props.careLevelRequested === CareLevel.ALS;
  }
  public get isBLS() {
    return this._props.careLevelRequested === CareLevel.BLS;
  }
  public get isCCT() {
    return this._props.careLevelRequested === CareLevel.CCT;
  }

  public isAnyAffiliationType(...affiliationType: AffiliationType[]) {
    return (
      this._props.affiliationType != null && affiliationType.includes(this._props.affiliationType)
    );
  }
  public isAnyMissionRole(...missionRoleType: MissionRoleType[]) {
    return this._props.missionRole != null && missionRoleType.includes(this._props.missionRole);
  }
  public isAnyCareLevelRequested(...careLevel: CareLevel[]) {
    return (
      this._props.careLevelRequested != null && careLevel.includes(this._props.careLevelRequested)
    );
  }

  constructor(props: RequesterProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        affiliationType: this._props.affiliationType,
        caller: this._props.caller,
        roleId: this._props.roleId,
        missionRole: this._props.missionRole,
        callbackNumber: this._props.callbackNumber,
        email: this._props.email,
        facilityId: this._props.facilityId,
        agencyLocationId: this._props.agencyLocationId,
        isNewAgencyLocation: this._props.isNewAgencyLocation,
        agencyLocationName: this._props.agencyLocationName,
        referringCompanyId: this._props.referringCompanyId,
        referringAgencyLocationId: this._props.referringAgencyLocationId,
        referralReasonId: this._props.referralReasonId,
        referringRecordNumber: this._props.referringRecordNumber,
        specialtyTeamNotes: this._props.specialtyTeamNotes,
        specialtyTeamId: this._props.specialtyTeamId,
        specialtyTeamMembers: this._props.specialtyTeamMembers,
        careLevelRequested: this._props.careLevelRequested,
        transferCenterId: this._props.transferCenterId,
        transferCenterNotes: this._props.transferCenterNotes,
        transportingAgencyLocationId: this._props.transportingAgencyLocationId,
        isDoublePatient: this._props.isDoublePatient,
        isWingToWing: this._props.isWingToWing,
        vetNumber: this._props.vetNumber,
        requestId: this._props.requestId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllRequesterEntities = (state: AppState) =>
  state.entityStores.requester.entities;

export const selectRequesterById = (id: string) => (state: AppState) =>
  state.entityStores.requester.entities.get(id);

export const selectRequesterMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.requester.meta.get(id);

export const selectRequesterAndMetadataById = (id: string) =>
  createSelector(selectRequesterById(id), selectRequesterMetadataById(id), (requester, meta) => ({
    requester,
    meta,
  }));

export function selectRequesterEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllRequesterEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, RequesterRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectRequesterEntitiesByAffiliationType(affiliationType: AffiliationType) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.affiliationType === affiliationType).valueSeq(),
  );
}

export function selectRequesterEntitiesByCaller(caller: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.caller === caller).valueSeq(),
  );
}

export function selectRequesterEntitiesByRoleId(roleId: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.roleId === roleId).valueSeq(),
  );
}

export function selectRequesterEntitiesByMissionRole(missionRole: MissionRoleType) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.missionRole === missionRole).valueSeq(),
  );
}

export function selectRequesterEntitiesByCallbackNumber(callbackNumber: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.callbackNumber === callbackNumber).valueSeq(),
  );
}

export function selectRequesterEntitiesByEmail(email: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.email === email).valueSeq(),
  );
}

export function selectRequesterEntitiesByFacilityId(facilityId: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.facilityId === facilityId).valueSeq(),
  );
}

export function selectRequesterEntitiesByAgencyLocationId(agencyLocationId: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.agencyLocationId === agencyLocationId).valueSeq(),
  );
}

export function selectRequesterEntitiesByIsNewAgencyLocation(isNewAgencyLocation: boolean) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.isNewAgencyLocation === isNewAgencyLocation).valueSeq(),
  );
}

export function selectRequesterEntitiesByAgencyLocationName(agencyLocationName: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.agencyLocationName === agencyLocationName).valueSeq(),
  );
}

export function selectRequesterEntitiesByReferringCompanyId(referringCompanyId: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.referringCompanyId === referringCompanyId).valueSeq(),
  );
}

export function selectRequesterEntitiesByReferringAgencyLocationId(
  referringAgencyLocationId: string,
) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.referringAgencyLocationId === referringAgencyLocationId).valueSeq(),
  );
}

export function selectRequesterEntitiesByReferralReasonId(referralReasonId: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.referralReasonId === referralReasonId).valueSeq(),
  );
}

export function selectRequesterEntitiesByReferringRecordNumber(referringRecordNumber: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.referringRecordNumber === referringRecordNumber).valueSeq(),
  );
}

export function selectRequesterEntitiesBySpecialtyTeamNotes(specialtyTeamNotes: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.specialtyTeamNotes === specialtyTeamNotes).valueSeq(),
  );
}

export function selectRequesterEntitiesBySpecialtyTeamId(specialtyTeamId: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.specialtyTeamId === specialtyTeamId).valueSeq(),
  );
}

export function selectRequesterEntitiesByCareLevelRequested(careLevelRequested: CareLevel) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.careLevelRequested === careLevelRequested).valueSeq(),
  );
}

export function selectRequesterEntitiesByTransferCenterId(transferCenterId: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.transferCenterId === transferCenterId).valueSeq(),
  );
}

export function selectRequesterEntitiesByTransferCenterNotes(transferCenterNotes: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.transferCenterNotes === transferCenterNotes).valueSeq(),
  );
}

export function selectRequesterEntitiesByTransportingAgencyLocationId(
  transportingAgencyLocationId: string,
) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities
      .filter((e) => e.transportingAgencyLocationId === transportingAgencyLocationId)
      .valueSeq(),
  );
}

export function selectRequesterEntitiesByIsDoublePatient(isDoublePatient: boolean) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.isDoublePatient === isDoublePatient).valueSeq(),
  );
}

export function selectRequesterEntitiesByIsWingToWing(isWingToWing: boolean) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.isWingToWing === isWingToWing).valueSeq(),
  );
}

export function selectRequesterEntitiesByVetNumber(vetNumber: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.vetNumber === vetNumber).valueSeq(),
  );
}

export function selectRequesterEntitiesByRequestId(requestId: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.requestId === requestId).valueSeq(),
  );
}

export function selectRequesterEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllRequesterEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
